/* College of the Rockies (COTR) J&B 5th curriculum mapped onto BC's
 * Exterior / Interior / Full Service levels (#2309).
 *
 * COTR and the NFPA 1010 provider assess the same Jones & Bartlett drills. The
 * only difference is the view: COTR groups individual drills by BC minimum
 * standard level, while the NFPA 1010 view groups whole chapters into FF I/II.
 * Every chapter below therefore keeps the canonical `ff1` / `ff2` storage cell
 * from skillDrillsNfpa1010.js. Switching providers changes no drill path.
 *
 * Source: COTR "BC Structure FF Min Standards JB 5th", recorded on #2309.
 * COTR written exams and Hazmat chapters 29-36 are separate work (#2312/#2311),
 * so this map records theory chapters but does not make an unimplemented exam a
 * sign-off prerequisite.
 */

const Nfpa1010SkillDrills = require('./skillDrillsNfpa1010');

const drillCodes = (chapter, numbers) => numbers.map(number => `jb-${chapter}-${number}`);
const range = (from, to) => Array.from({length: to - from + 1}, (_, index) => from + index);

const COTR_LEVEL_MAP = {
    exterior: {
        label: 'Exterior',
        certs: [
            {path: 'provincial.ics100', responsibility: 'department', required: true},
            {path: 'provincial.trafficControl', responsibility: 'department', required: true},
            {path: 'provincial.gasSafety', responsibility: 'department', required: true},
            {path: 'provincial.electricalSafety', responsibility: 'department', required: true},
            {path: 'provincial.hazmatAwareness', responsibility: 'department', required: true},
            {path: 'provincial.sppWff1', responsibility: 'department', required: true},
            {path: 'provincial.firstAid', responsibility: 'department', required: false},
            {path: 'provincial.apparatusDriverOperator', responsibility: 'department', required: false}
        ],
        units: [
            {key: 'service-comms', label: 'Fire Service / Communications', theoryChapters: [1], drills: drillCodes(4, range(1, 2))},
            {key: 'health-ppe', label: 'Health & Safety / PPE & SCBA', drills: [...drillCodes(2, range(1, 2)), ...drillCodes(3, range(1, 14))]},
            {key: 'behaviour-building', label: 'Fire Behaviour / Building Construction', theoryChapters: [5, 6], drills: []},
            {key: 'tools', label: 'Tools & Equipment', drills: drillCodes(7, [1])},
            {key: 'ropes', label: 'Ropes & Knots', drills: drillCodes(8, range(1, 21))},
            {key: 'water', label: 'Water Supply', drills: drillCodes(10, range(1, 7))},
            {key: 'hose', label: 'Hose, Appliances & Nozzles', drills: drillCodes(11, range(1, 14))},
            {
                key: 'evolutions',
                label: 'Supply & Attack Line Evolutions',
                drills: drillCodes(13, range(1, 28)),
                notes: {'jb-13-20': 'Exterior stairways only', 'jb-13-21': 'Exterior stairways only'}
            },
            {key: 'ladders', label: 'Ladders', drills: drillCodes(12, range(1, 15))},
            {key: 'forcible-entry', label: 'Forcible Entry — Exterior', drills: drillCodes(15, range(1, 23))},
            {key: 'ventilation', label: 'Ventilation — Exterior', drills: drillCodes(16, [1, 2, 3, 4, 6, 13, 14])},
            {key: 'suppression', label: 'Fire Suppression — Exterior', drills: drillCodes(14, [6, 7, 8, 9, 10, 11, 13, 14, 15])},
            {key: 'extinguishers', label: 'Fire Extinguishers — Exterior', drills: drillCodes(9, [1, 2, 3, 4, 5, 6, 8])},
            {key: 'wildland', label: 'Wildland / Ground Cover', drills: drillCodes(21, [1])},
            {key: 'command', label: 'Rehab / Establishing & Transferring Command', theoryChapters: [20], drills: drillCodes(22, range(1, 3))}
        ]
    },
    interior: {
        label: 'Interior',
        certs: [
            {path: 'shared.hazmatOps', responsibility: 'cotr', required: true}
        ],
        units: [
            {key: 'forcible-entry', label: 'Forcible Entry — Interior', drills: drillCodes(15, [24])},
            {key: 'ventilation', label: 'Ventilation — Interior', drills: drillCodes(16, [5, 7, 8, 9, 10, 11, 12])},
            {key: 'extinguishers', label: 'Fire Extinguishers — Interior', drills: drillCodes(9, [9])},
            {key: 'suppression', label: 'Fire Suppression — Interior', drills: drillCodes(14, [1, 2, 3, 4, 5, 12])},
            {key: 'search-rescue', label: 'Search & Rescue', drills: drillCodes(17, range(1, 19))},
            {key: 'advanced-suppression', label: 'Advanced Fire Suppression', drills: drillCodes(23, range(1, 8))},
            {key: 'survival', label: 'Firefighter Survival', drills: drillCodes(19, range(1, 11))},
            {key: 'salvage', label: 'Salvage & Overhaul', drills: drillCodes(18, range(1, 17))},
            {key: 'extrication', label: 'Vehicle Rescue & Extrication', drills: drillCodes(26, range(1, 10))}
        ]
    },
    fullService: {
        label: 'Full Service',
        certs: [
            {path: 'fireOfficer1', responsibility: 'department', required: false},
            {path: 'fireOfficer1.instructor1', responsibility: 'department', required: false},
            {path: 'iso.iso', responsibility: 'department', required: false}
        ],
        units: [
            {key: 'detection', label: 'Detection, Suppression & Smoke Control', theoryChapters: [24], drills: []},
            {key: 'life-safety', label: 'Fire & Life Safety Initiatives', drills: drillCodes(25, range(1, 6))},
            {key: 'special-rescue', label: 'Assisting Special Rescue Teams', drills: drillCodes(27, range(1, 3))},
            {key: 'investigations', label: 'Understanding Fire Investigations', theoryChapters: [28], drills: []}
        ]
    }
};

const canonicalChapters = [...Nfpa1010SkillDrills.ff1.chapters, ...Nfpa1010SkillDrills.ff2.chapters];
const chapterByNumber = new Map(canonicalChapters.map(chapter => [chapter.chapter, chapter]));
const drillByCode = new Map(canonicalChapters.flatMap(chapter => chapter.drills.map(drill => [drill.code, {chapter, drill}])));

const chaptersFor = definition => definition.units.flatMap(unit => {
    const codesByChapter = new Map();
    for (const code of unit.drills) {
        const source = drillByCode.get(code);
        if (!source) continue;
        if (!codesByChapter.has(source.chapter.chapter)) codesByChapter.set(source.chapter.chapter, []);
        codesByChapter.get(source.chapter.chapter).push({...source.drill, ...(unit.notes?.[code] ? {note: unit.notes[code]} : {})});
    }

    for (const chapter of unit.theoryChapters || []) {
        if (!codesByChapter.has(chapter)) codesByChapter.set(chapter, []);
    }

    return [...codesByChapter.entries()].map(([chapterNumber, drills]) => {
        const source = chapterByNumber.get(chapterNumber);
        return {
            ...source,
            // COTR exams are deliberately not modelled yet (#2312). The source
            // chapter's exam/module remains part of the canonical NFPA 1010 view.
            exam: undefined,
            theoryOnly: false,
            drills,
            unit: unit.key,
            unitLabel: unit.label
        };
    });
});

const section = (levelKey, key, order) => ({
    key,
    levelKey,
    label: COTR_LEVEL_MAP[levelKey].label,
    order,
    ladder: 'cotr',
    documentSet: 'jb-1010',
    sheetEdition: 'jb-ca5',
    chapters: chaptersFor(COTR_LEVEL_MAP[levelKey]),
    exams: []
});

const CotrSkillDrills = {
    cotrExterior: section('exterior', 'cotrExterior', 1),
    cotrInterior: section('interior', 'cotrInterior', 2),
    cotrFullService: section('fullService', 'cotrFullService', 3)
};

module.exports = {COTR_LEVEL_MAP, CotrSkillDrills};
